import { atom } from 'jotai';
import {
  AbyssViewerCallbacks,
  CameraTargetExtendedProps,
  CameraTargetProps,
  CurrentImageProps,
  VisibilityBoxProps,
} from '@fabric-web/3d-viewer';
import { EsSheetData } from '@/components/EsSheet/EquipmentStrategyOnboarding/shared/UploadPanel';

import {
  GoverningValueBlister,
  PointOfInterest,
  GetAssemblyDetailsQuery,
  BlisterHeightType,
  GetAllAssembliesForEvaluationQuery,
  DefectLegend,
  GetAssembliesByTemplateFieldsFiltersForEquipmentsTabQuery,
  Box,
  PointOfInterestTemplateTypeEnum,
  GetAllDecksQuery,
  GetStructureLocationsForInspectionDataLoaderQuery,
  GetStructureForInspectionDataLoaderQuery,
  LocationLayer,
  StructureForRiskAssessmentQuery,
  AllAssemblyTemplatesForRiskAssessmentQuery,
  GetTemplateByTypeQuery,
  UnitSystemEnum,
  ReportDetailsQuery,
  GetStructureFeaturesQuery,
  GetPaintRegionStatusLabelsQuery,
  GetPointOfInterestGroupedByWorkpackQuery,
  GetAssembliesByTemplateFieldsFiltersForFilterSideBarQuery,
  GetInspectionPlansQuery,
} from '@/__generated__/graphql';
import {
  Point3d,
  SnackbarMessage,
  Viewpoint,
  ToggleableDefectLegend,
  AllPointOfInterestTemplate,
  AnalysisDrawerRight,
  AnalysisDrawerLeft,
  TemplateWithPOIs,
  AnalysisStructureRelationships,
  AssemblyTemplatesQuery,
  BlendOpacityModes,
  PaintRegionGroup,
  Filters,
  LastDeeplinkParameters,
  AssemblyCameraPose,
  AppliedAssemblyFilters,
  AssemblyTemplateQuery,
  GroupByResult,
  PointOfInterestGroupBy,
  TemplateFilterInput,
} from '@/types';
import { DEFAULT_VISIBILITY_BOX } from '@/constants';
import { UIMessage } from 'ai';
import { CustomFilterQueryVars } from '@/hooks/useCustomFilters';
import { POI_MARKER_LABEL_BACKGROUND_COLOR } from '@/theme/colors';
import { SelectedValues } from '../../../shared/FilterAndSearchPanel/FilterAndSearchPanel';

export const selectedStructureRiskAssessment = atom<
  StructureForRiskAssessmentQuery['structure'] | undefined
>(undefined);

export const inspectionMetadata = atom<
  GetStructureForInspectionDataLoaderQuery['structure'] | undefined
>(undefined);

export const partLegends = atom<{ [key: string]: string }>({});

export const structureLocationsMap = atom<
  Map<string, GetStructureLocationsForInspectionDataLoaderQuery['allLocations'][0]> | undefined
>(undefined);

export const structureLocations = atom<
  GetStructureLocationsForInspectionDataLoaderQuery['allLocations'] | undefined
>(undefined);

// Viewpoints drawer maintains this state
export const filteredViewpoints = atom<
  GetStructureLocationsForInspectionDataLoaderQuery['allLocations'] | undefined
>(undefined);
export const viewpointsSearchValue = atom<string>('');

export const imageLayer = atom<LocationLayer | undefined>(undefined);

export const structureDecks = atom<GetAllDecksQuery['allDecks'] | undefined>(undefined);

export const areSphericalsVisible = atom<boolean>(true);

export const neighbouringImagesMode = atom<{
  isEnabled: boolean;
  showFrames: boolean;
  showImages: boolean;
}>({
  isEnabled: true,
  showFrames: true,
  showImages: false,
});

export const areDecksVisible = atom<boolean>(false);

export const arePoisVisible = atom<boolean>(false);

export const showOnlySelectedPoi = atom<boolean>(false);

export const snackbarMessage = atom<SnackbarMessage>({ shouldShow: false });

export const globalCallbacks = atom<AbyssViewerCallbacks | undefined>(undefined);

export const cameraTarget = atom<CameraTargetProps | undefined>(undefined);

export const viewerCurrentSpherical = atom<CurrentImageProps | undefined>(undefined);

export const viewerNextCurrentSpherical = atom<CurrentImageProps | undefined>(undefined);

export const selectedAssemblyId = atom<string | undefined>(undefined);

export const selectedAssemblyDetails = atom<GetAssemblyDetailsQuery['assembly'] | undefined>(
  undefined
);

export const selectedAssemblyIds = atom<string[]>([]);

export const isAllComponentsSelected = atom<boolean>(false);

export const unselectedAssemblyIds = atom<string[]>([]);

export const selectedAssemblyName = atom<string | undefined>(undefined);

export const isUncontextualised = atom<boolean>(false);

export const showAssemblyCadModel = atom<boolean>(false);

export const showPhotogrammetryTiles = atom<boolean>(false);

export const showPointCloud = atom<boolean>(true);

export const showPointCloudInSpherical = atom<boolean>(true);

export const showPointsOutsideViewpoint = atom<boolean>(false);
export const hoveredAssemblyId = atom<string | undefined>(undefined);

export const unitSystem = atom<UnitSystemEnum>(UnitSystemEnum.Imperial);

export const systemTest = atom<boolean>(false);

export const viewpointsByAssemblyId = atom<{ [assemblyId: string]: Viewpoint[] }>({});

export const focalPointsByAssemblyId = atom<{
  [assemblyId: string]: {
    focalPoint?: Point3d;
    cameraPose?: AssemblyCameraPose;
    box?: Box;
  };
}>({});
export const governingValueByAssemblyId = atom<{
  [assemblyId: string]: {
    blister?: Partial<PointOfInterest> | null;
    calculations?: GoverningValueBlister | null;
    pointOfInterest?: { id: string } | null;
  };
}>({});
export const filteredAssemblyIds = atom<string[]>([]);

export const displayAnimationEditor = atom<boolean>(false);

export const selectedStructureDefectsLegends = atom<Array<ToggleableDefectLegend> | undefined>(
  undefined
);

export const nonSelectedDefectsIsEnabled = atom<boolean>(true);

export const allPointOfInterestTemplates = atom<AllPointOfInterestTemplate>([]);

export const allVoiTemplates = atom<AllPointOfInterestTemplate>((get) => {
  const allPointOfInterestTemplatesValue = get(allPointOfInterestTemplates);
  if (!allPointOfInterestTemplatesValue) return [];
  const vois = allPointOfInterestTemplatesValue.filter(
    (template) => template.type === PointOfInterestTemplateTypeEnum.VolumeOfInterest
  );
  return vois;
});

export const poiTemplateIcons = atom<{ name: string; icon: string; color: string; key: string }[]>(
  []
);

// POI type mappings for Equipment Table (key: lowercase POI type, value: color/icon)
export const poiTypeToColorMap = atom<Map<string, string>>(new Map());
export const poiTypeToIconMap = atom<Map<string, string>>(new Map());

export const allAssemblyTemplates = atom<AssemblyTemplatesQuery | undefined>(undefined);

/**
 * @deprecated Use the `useSelectedAssemblyTemplateId` hook instead of accessing this atom directly.
 *
 * This atom is meant for internal state management and should not be directly set or accessed outside the custom hook.
 * Manually setting this atom's value will affect the currently selected assembly template globally,
 * which can cause bugs or unexpected behavior, especially in shared or concurrent environments.
 * Instead, always use the `useSelectedAssemblyTemplateId` hook to read the selected assembly template ID.
 */
export const selectedAssemblyTemplateId = atom<string | undefined>(undefined);

export const visitedViewpointIDs = atom<Array<string>>([]);

export const selectedAssemblyInfo = atom<string | undefined>(undefined);

export const drawerLeft = atom<AnalysisDrawerLeft>({
  visibility: false,
  query: true,
});

export const drawerRight = atom<AnalysisDrawerRight>({
  equipment: true,
  viewpoints: false,
  measuringTool: false,
  findViewpointsTool: false,
});

export const drawerRightPreviousState = atom<keyof AnalysisDrawerRight>('equipment');
export const selectedReport = atom<ReportDetailsQuery['report'] | undefined>(undefined);
export const currentVisibilityBox = atom<VisibilityBoxProps>(DEFAULT_VISIBILITY_BOX);

export const voiVisibilityBox = atom<VisibilityBoxProps | undefined>();
export const selectedTemplateFiltersState = atom<TemplateFilterInput[]>([]);

// Currently active template field id used by search/filter UI to show options
export const activeTemplateFieldId = atom<string | null>(null);

export const selectedFilteredDeckIds = atom<string[]>([]);

export const selectedFilteredPaintRegionIds = atom<string[]>([]);

export const applyFilters = atom<boolean>(false);

export const insightsSearchBarSearchTerm = atom<string>('');

export const insightsSearchBarRangeSearchTerm = atom<string>('');

export const refetchEquipmentTable = atom<number>(0);

export const inspectionPlansState = atom<GetInspectionPlansQuery['getInspectionPlans']>([]);
export const selectedInspectionPlanId = atom<string>('');

export const appliedAssemblyFilters = atom<AppliedAssemblyFilters>({
  templateFilters: [],
  deckFilters: [],
  paintRegionFilters: [],
  workpackFilters: [],
  poiTypeFilters: [],
});

export const refetchUpdatedPois = atom<boolean>(false);
export const showPaintRegionsState = atom<boolean>(false);

export const showCameraPositionsState = atom<boolean>(true);

export const appliedHeatmapValue = atom<string>('');

export const markerScaleSize = atom<number>(2);

export const heatmapImageState = atom<string>('');

export const paintRegionVisibilityBox = atom<VisibilityBoxProps | undefined>(undefined);
export const paintRegionOutline = atom<string>('');

export const hasRiskAssessment = atom<boolean>(false);

export const selectedFilteredWorkpackIds = atom<string[]>([]);
/** POI template document IDs (Insights / Review filters) */
export const selectedFilteredPoiTypes = atom<string[]>([]);
export type DateFilterType = 'range' | 'single' | null;
export type DateFilterValue =
  | { type: 'range'; startDate: string | null; endDate: string | null }
  | { type: 'single'; date: string | null };
export const dateFilters = atom<Record<string, DateFilterValue>>({});
/** Last-applied date filters (set when user clicks Apply Query). Draft selections live in `dateFilters`. */
export const appliedDateFilters = atom<Record<string, DateFilterValue>>({});
export const filters = atom<Filters | undefined>(undefined);

export const hasRgb = atom<boolean>(false);

export const structureRelationship = atom<AnalysisStructureRelationships>({
  annotationIdToAssemblyId: {},
  annotationIdToAnnotation3d: {},
  annotation3dToAnnotationId: {},
  assemblyIdToAnnotation3dArray: {},
  assemblyIdAndLocationIdToAnnotation3dArray: {},
  assemblyIdToAssembly3d: {},
  partsByAnnotationId: {},
  parts: {},
  loaded: false,
});

export const blendOpacityCombined = atom<BlendOpacityModes>({
  viewer: 255,
  assembly: 255,
  spherical: 255,
});
export const pointOfInterestTemplates = atom<TemplateWithPOIs[]>([]);

export const selectedRiskAssessmentFilters = atom<{ id: string; value: string }[]>([]);

export const riskAssessmentSearchTerm = atom<string>('');

export const riskAssessmentAssemblyTemplate = atom<
  AllAssemblyTemplatesForRiskAssessmentQuery['allTemplates'][0] | undefined
>(undefined);

export const structureTemplate = atom<GetTemplateByTypeQuery['allTemplates'][0] | undefined>(
  undefined
);

export const isTemplateFieldDataUpdated = atom<boolean>(false);

export const maintainSelectedEquipments = atom<boolean>(false);

export const isCriticalityLevelDialogOpen = atom<boolean>(false);

export const assembliesCount = atom<number>(0);

export const allAssembliesForReview =
  atom<GetAllAssembliesForEvaluationQuery['getAssembliesByTemplateFieldsFilters']>();

export const allAssembliesForInsights = atom<
  GetAssembliesByTemplateFieldsFiltersForFilterSideBarQuery['getAssembliesByTemplateFieldsFilters']
>([]);
export const assembliesLoading = atom<boolean | undefined>(undefined);

export const pickingMapsUpdateSequence = atom<number>(0);

export const structureFeatures = atom<GetStructureFeaturesQuery['structure'] | undefined>(
  undefined
);

export const customBlocksEnabled = atom<boolean>(false);

export const customBlocksNames = atom<string[]>([]);

/**
  Used to filter what kind of paint regions we want to fetch.
 */
export const selectedPaintRegionGroupId = atom<string | undefined>(undefined);
export const selectedPaintRegionGroup = atom<PaintRegionGroup | undefined | null>((get) => {
  const selectedPaintRegionGroupIdValue = get(selectedPaintRegionGroupId);
  const allPaintRegionGroupsValue = get(allPaintRegionGroups);
  return allPaintRegionGroupsValue.find((group) => group.id === selectedPaintRegionGroupIdValue);
});
export const allPaintRegionGroups = atom<PaintRegionGroup[]>([]);

/**
 * Used to track the selected paint block for showing history in the right sidebar
 */
export const selectedPaintBlockHistory = atom<Record<string, string | number> | undefined>(
  undefined
);

export const isSavedBlocksSelected = atom<boolean>(false);
export const chatState = atom<UIMessage[]>([]);

export const chatOpen = atom<boolean>(false);

export const poiCountsByType = atom<Array<{ type: string; count: number }>>([]);

export const insightsExplorerLoadingState = atom<boolean>(false);

export const lastDeeplinkUrlParameters = atom<LastDeeplinkParameters | undefined>(undefined);

export const structureBlisterHeightType = atom<BlisterHeightType | undefined>(undefined);

export const paintRegionStatusLabels = atom<GetPaintRegionStatusLabelsQuery['setting'] | undefined>(
  undefined
);

export const poiStatusLabels = atom<GetPaintRegionStatusLabelsQuery['setting'] | undefined>(
  undefined
);

export const workpackStatusLabels = atom<GetPaintRegionStatusLabelsQuery['setting'] | undefined>(
  undefined
);

export const showPointOfInterestMarkerLabels = atom<boolean>(false);
export const showPointOfInterestPointer = atom<boolean>(false);
export const swivelMarkerLabels = atom<boolean>(false);
export const markerLabelScale = atom<number>(1);
export const markerLabelHeightVariance = atom<number>(5);
export const componentDetailsById = atom<
  | {
      [key: string]: Record<string, string>;
    }
  | undefined
>({});

export const componentAreasById = atom<{ [key: string]: number } | undefined>(undefined);

export const componentsDetailsLoading = atom<boolean>(true);
export const componentsDefectLegends = atom<DefectLegend[] | undefined>(undefined);

export const customFilterQueryVarsState = atom<CustomFilterQueryVars>({});

export const filteredAssemblies =
  atom<
    GetAssembliesByTemplateFieldsFiltersForEquipmentsTabQuery['getAssembliesByTemplateFieldsFilters']
  >();

export const selectedAssemblyLoading = atom<boolean>(false);

export const assemblyTemplate = atom<AssemblyTemplateQuery | undefined>((get) => {
  const selectedAssemblyTemplateIdValue = get(selectedAssemblyTemplateId);
  const allAssemblyTemplatesValue = get(allAssemblyTemplates);
  return allAssemblyTemplatesValue?.find((item) => item.id === selectedAssemblyTemplateIdValue);
});

export const isCurrentAssemblyTemplateCombined = atom<boolean>((get) => {
  const assemblyTemplateValue = get(assemblyTemplate);
  return (
    !!assemblyTemplateValue?.includedTemplateIds &&
    assemblyTemplateValue?.includedTemplateIds.length > 0
  );
});

const combinedSelectedTemplateIds = atom<string[]>((get) => {
  const assemblyTemplateValue = get(assemblyTemplate);

  if (!assemblyTemplateValue?.id) return [];

  if (
    assemblyTemplateValue?.includedTemplateIds &&
    assemblyTemplateValue?.includedTemplateIds?.length > 1
  )
    return assemblyTemplateValue?.includedTemplateIds;

  return [assemblyTemplateValue?.id];
});

export const combinedSelectedTemplates = atom<Array<AssemblyTemplateQuery | undefined>>((get) => {
  const allTemplatesValue = get(allAssemblyTemplates);
  const combinedTemplateIdsValue = get(combinedSelectedTemplateIds);

  if (!combinedTemplateIdsValue || combinedTemplateIdsValue.length === 0) return [];
  return combinedTemplateIdsValue.map((id) =>
    (allTemplatesValue || []).find((template) => template.id === id)
  );
});
export const poiMarkerLabelBgColor = atom<string>(POI_MARKER_LABEL_BACKGROUND_COLOR);

export const allReviewPointsOfInterest = atom<PointOfInterest[]>([]);
export const selectedReviewPointsOfInterest = atom<string[]>([]);
export const isAllReviewPointsOfInterestSelected = atom<boolean>(false);

export const selectedReviewPoiFilters = atom<SelectedValues>({});
export const selectedReviewPaintRegionIds = atom<string[]>([]);
export const selectedReviewWorkpackIds = atom<string[]>([]);
export const reviewPoiSearchTerm = atom<string>('');
export const selectedReviewPoiTypeId = atom<string | undefined>();
export const selectedReviewPoiTypes = atom<string[]>([]);

export const selectedReportFilters = atom<SelectedValues>({});

export const createdByMeFilter = atom<boolean>(false);

export const sideBySideModeEnabled = atom<boolean>(false);
export const selectedIsometricFileId = atom<string | undefined>(undefined);
export const screenshotMode = atom<string | undefined>(undefined);

export const checkedGroupedPois = atom<GroupByResult<PointOfInterestGroupBy>>({});
export const currentCameraPose = atom<CameraTargetExtendedProps | undefined>(undefined);

export const selectedFieldByType = atom<Record<string, { label: string; value: string }>>({
  Other: {
    label: 'Status',
    value: 'status',
  },
});

export const structureDefaultAssemblyId = atom<string | undefined>(undefined);

export const structurePipesByStructureId = atom<Record<string, Record<string, number>>>({});

export const poiSummaryChartsConfigByStructureId = atom<
  Record<string, Record<string, string[] | null | undefined>>
>({});

// All workpacks list (set from Workpacks.tsx when data is fetched)
type GroupedWorkpack = NonNullable<
  GetPointOfInterestGroupedByWorkpackQuery['allWorkpacks']
>[number];
export const allWorkpacks = atom<GroupedWorkpack[]>([]);

export const showUncalibratePaintBlockdData = atom<boolean>(false);

export const originalEsSheet = atom<EsSheetData | null>(null);
export const editableEsSheet = atom<EsSheetData | null>(null);
const selectedEsSheetTab = atom<number>(1);
export const isLegacyEsSheet = atom((get) => !!get(selectedEsSheetTab));

export const isBulkReferenceDrawingsOpen = atom<boolean>(false);

// Holds the first spherical ID where VOI creation started
export const firstSelectedSphericalIdForVoi = atom<string | undefined>(undefined);
